import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { COLLECTIONS, setPublishStatus } from "../utils/cmsSchema";
import { revalidateFor } from "../utils/revalidatePublic";
import FormShell, {
  FormSection,
  PublishToggle,
  TextField,
} from "../components/FormShell";
import FileDropzone from "../components/FileDropzone";

// Edit form for one broker entry in the `brokers` collection. Deliberately
// minimal — brokers have no public detail page, no bio, no SEO. The
// public /aboutUs slideshow reads only { name, photoUrl, sortOrder,
// photoObjectPosition } from published entries.

const SCHEMA = COLLECTIONS.brokers;

export default function BrokerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);

  const isNew = !id;
  const docId = useMemo(() => id || v4(), [id]);

  const [form, setForm] = useState({
    name: "",
    photoUrl: "",
    photoObjectPosition: "",
    sortOrder: 0,
    published: false,
  });
  const [originalDoc, setOriginalDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const trackUpload = (b) =>
    setUploadingCount((c) => Math.max(0, c + (b ? 1 : -1)));

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      const data = await getDocData(SCHEMA.key, id);
      if (!active) return;
      if (!data) {
        toast.error("Not found.");
        navigate(SCHEMA.route);
        return;
      }
      setOriginalDoc(data);
      setForm({
        name: data.name || data.title || "",
        photoUrl: data.photoUrl || "",
        photoObjectPosition: data.photoObjectPosition || "",
        sortOrder: Number.isFinite(data.sortOrder) ? data.sortOrder : 0,
        published: data[SCHEMA.statusField] === SCHEMA.publishedValue,
      });
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id, isNew, navigate]);

  const handleSave = async (publishOverride) => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!form.photoUrl) {
      toast.error("Please upload a photo before saving.");
      return;
    }
    if (uploadingCount > 0) {
      toast.error("Wait for the photo upload to finish.");
      return;
    }
    setIsSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const published =
        typeof publishOverride === "boolean" ? publishOverride : form.published;
      const trimmedName = form.name.trim();
      const sortOrderNumeric = Number(form.sortOrder);
      const payload = {
        ...(originalDoc || {}),
        name: trimmedName,
        // `title` mirror so ContentCard picks up the display name via
        // its shared `doc.title` accessor. Same trick used for leaders.
        title: trimmedName,
        titleLower: trimmedName.toLowerCase(),
        photoUrl: form.photoUrl || "",
        photoObjectPosition: form.photoObjectPosition.trim(),
        sortOrder: Number.isFinite(sortOrderNumeric) ? sortOrderNumeric : 0,
        updatedAt: nowIso,
        lastUpdatedBy: currentUser?.email || null,
      };
      setPublishStatus(payload, SCHEMA, published);
      if (isNew) {
        payload.createdAt = nowIso;
        payload.createdBy = currentUser?.email || null;
        payload.type = SCHEMA.key;
      }
      await setDocData(SCHEMA.key, docId, payload);
      revalidateFor(SCHEMA.key);
      toast.success(published ? "Broker published." : "Broker saved.");
      setForm((p) => ({ ...p, published }));
      if (isNew) {
        navigate(`${SCHEMA.route}/${docId}`, { replace: true });
      } else {
        setOriginalDoc(payload);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  const saveDisabled = isSaving || uploadingCount > 0;

  return (
    <FormShell
      backTo={SCHEMA.route}
      backLabel="Back to Brokers"
      eyebrow={isNew ? "New Broker" : "Editing Broker"}
      title={form.name || "Untitled Broker"}
      footer={
        <>
          <button
            type="button"
            onClick={() => navigate(SCHEMA.route)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {form.published ? "Update & Keep Published" : "Publish"}
          </button>
        </>
      }
    >
      <div className="lg:col-span-2 space-y-6">
        <FormSection
          title="Broker"
          description="Just a name and a portrait — no public bio, no detail page."
        >
          <TextField
            label="Full name"
            required
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="e.g. Juan Dela Cruz"
          />
        </FormSection>
      </div>

      <div className="space-y-6">
        <FormSection
          title="Photo"
          description="Portrait shown in the About Us slideshow. Square or portrait orientation works best; the grid crops to a square."
        >
          <FileDropzone
            kind="image"
            collection={SCHEMA.key}
            docId={docId}
            value={form.photoUrl}
            onChange={(url) => setField("photoUrl", url)}
            onUploadingChange={trackUpload}
          />
          <TextField
            label="Photo object position (optional)"
            value={form.photoObjectPosition}
            onChange={(e) => setField("photoObjectPosition", e.target.value)}
            placeholder="center top"
            hint='Nudges how the photo crops inside the frame — e.g. "0px -10px" to push the face down slightly. Defaults to "center top" if empty.'
          />
        </FormSection>

        <FormSection title="Visibility">
          <PublishToggle
            value={form.published}
            onChange={(v) => setField("published", v)}
          />
        </FormSection>

        <FormSection
          title="Ordering"
          description="Lower numbers appear first in the slideshow rotation pool. Use 0 for the first N to always show, higher numbers to rotate less often."
        >
          <TextField
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setField("sortOrder", e.target.value)}
            placeholder="0"
          />
        </FormSection>
      </div>
    </FormShell>
  );
}
